// src/gac/GetAllLabelsPanel.js
// Extracted from GlobalActionCenter.js (GAC modularization, phase 1). No behaviour change.
import React, { useState, useRef, useEffect } from 'react';
import axios from '../axiosInstance';
import '../GlobalActionCenterStyles.css';

const GetAllLabelsPanel = ({
    globalCompany,
    globalSelectedStore,
    results,
    setResults,
    errors,
    setErrors,
}) => {
    // Get All Labels state
    const [fetchedLabels, setFetchedLabels] = useState([]);
    const [labelsLoading, setLabelsLoading] = useState(false);
    const [labelSearchFilter, setLabelSearchFilter] = useState('');
    const [labelSort, setLabelSort] = useState({ column: 'labelCode', direction: 'asc' });
    const [labelFilterType, setLabelFilterType] = useState('all'); // 'all', 'assigned', or 'unassigned'
    const [autoDownload, setAutoDownload] = useState(false); // auto-download CSV when fetch completes
    const [onlineOnly, setOnlineOnly] = useState(false); // filter to only show online labels
    const [labelsPage, setLabelsPage] = useState(1);
    const LABELS_PER_PAGE = 100;
    const [produceLinkFile, setProduceLinkFile] = useState(false); // produce .link file alongside CSV

    // Fetch labels for a store (assigned, unassigned, or both based on labelFilterType)
    const fetchLabels = async () => {
        if (!globalCompany) {
            alert('Please select a company first');
            return;
        }
        if (!globalSelectedStore) {
            alert('Please select a store first');
            return;
        }

        setLabelsLoading(true);
        setFetchedLabels([]);
        setErrors([]);
        setLabelsPage(1);

        // SSE path — must live outside try/finally so the finally block doesn't kill the spinner
        if (labelFilterType === 'all') {
            const query = new URLSearchParams({ companyCode: globalCompany.companyCode, storeCode: globalSelectedStore.store, fetchAll: 'true' });
            const es = new EventSource(`/api/global-action/actions/all-labels?${query}`);

            es.addEventListener('batch', (e) => {
                const { type, labels } = JSON.parse(e.data);
                setFetchedLabels(prev => [...prev, ...labels]);
                setResults(prev => {
                    const prevSummary = prev?.summary || { assignedCount: 0, unassignedCount: 0 };
                    const assignedCount = type === 'assigned' ? (prevSummary.assignedCount || 0) + labels.length : (prevSummary.assignedCount || 0);
                    const unassignedCount = type === 'unassigned' ? (prevSummary.unassignedCount || 0) + labels.length : (prevSummary.unassignedCount || 0);
                    return { summary: { assignedCount, unassignedCount, total: assignedCount + unassignedCount, type: 'all', message: `Found ${assignedCount} assigned + ${unassignedCount} unassigned labels` } };
                });
            });

            es.addEventListener('complete', (e) => {
                const { assignedCount, unassignedCount, total } = JSON.parse(e.data);
                setResults({ summary: { assignedCount, unassignedCount, total, type: 'all', message: `Found ${assignedCount} assigned + ${unassignedCount} unassigned labels` } });
                setLabelsLoading(false);
                es.close();
            });

            es.addEventListener('error', () => { setLabelsLoading(false); es.close(); });
            return;
        }

        try {
            const params = {
                companyCode: globalCompany.companyCode,
                storeCode: globalSelectedStore.store,
                fetchAll: 'true'
            };

            const endpoint = labelFilterType === 'assigned'
                ? 'global-action/actions/assigned-labels'
                : 'global-action/actions/unassigned-labels';

            const response = await axios.get(endpoint, { params });

            if (response.data.success) {
                const labels = (response.data.labels || []).map(l => ({ ...l, _labelType: labelFilterType }));
                setFetchedLabels(labels);
                setResults({
                    summary: {
                        total: labels.length,
                        type: labelFilterType,
                        message: response.data.message
                    }
                });
            } else {
                setErrors([response.data.message || `Failed to fetch ${labelFilterType} labels`]);
            }
        } catch (error) {
            console.error(`[${labelFilterType} Labels] Error:`, error);
            setErrors([error.response?.data?.message || `Failed to fetch ${labelFilterType} labels`]);
        } finally {
            setLabelsLoading(false);
        }
    };

    // Export labels to CSV
    const exportLabelsCsv = () => {
        if (fetchedLabels.length === 0) {
            alert('No labels to export. Fetch labels first.');
            return;
        }

        // Apply online-only filter if enabled
        // Logic: All assigned labels (online & offline) + Unassigned labels (online only)
        let labelsToExport = fetchedLabels;
        if (onlineOnly) {
            labelsToExport = fetchedLabels.filter(l =>
                l._labelType === 'assigned' || l.networkStatus === true
            );
        }

        if (labelsToExport.length === 0) {
            alert('No labels to export after applying filters.');
            return;
        }

        // CSV headers - include Type column for 'all', Article ID for assigned/all
        const includeArticle = labelFilterType === 'assigned' || labelFilterType === 'all';
        const includeType = labelFilterType === 'all';

        const headers = [
            'Label Code',
            ...(includeType ? ['Type'] : []),
            ...(includeArticle ? ['Article ID'] : []),
            'Label Model',
            'Firmware',
            'Signal',
            'Battery',
            'Temperature',
            'Gateway',
            'Store',
            'Last Response Time',
            'Network Status'
        ];

        // Build CSV rows
        const rows = labelsToExport.map(label => {
            return [
                label.labelCode || '',
                ...(includeType ? [label._labelType === 'assigned' ? 'Assigned' : 'Unassigned'] : []),
                ...(includeArticle ? [label.articleId || label.articleList?.[0] || ''] : []),
                label.labelModel || '',
                label.firmwareVersion || '',
                label.signal || '',
                label.battery || '',
                label.temperature || '',
                label.gateway || '',
                label.store || '',
                label.lastResponseTime || '',
                label.networkStatus === true ? 'Online' : label.networkStatus === false ? 'Offline' : 'N/A'
            ];
        });

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; // EST local date
        const onlineSuffix = onlineOnly ? '-online' : '';
        link.setAttribute('href', url);
        link.setAttribute('download', `${labelFilterType}-labels${onlineSuffix}-${globalCompany?.companyCode}-${globalSelectedStore?.store}-${timestamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Also export .link file if checkbox is checked
        if (produceLinkFile) {
            exportLabelsLink(labelsToExport);
        }
    };

    // Export labels to .link file format (semicolon-delimited: Store Code;Label Code;Template Name;Product ID)
    const exportLabelsLink = (labels) => {
        if (!labels || labels.length === 0) return;

        // Only include assigned labels (those with articleId)
        const assignedLabels = labels.filter(l => l.articleId || l.articleList?.[0]);

        if (assignedLabels.length === 0) {
            alert('No assigned labels to export to .link file. The .link format requires labels with product assignments.');
            return;
        }

        // Build .link content: Store Code;Label Code;Template Name;Product ID
        const header = 'Store Code;Label Code;Template Name;Product ID';
        const rows = assignedLabels.map(label => {
            const storeCode = globalSelectedStore?.store || '';
            const labelCode = label.labelCode || '';
            const templateName = label.templateName || label.template || '2_2_BWR_Discount_F_M'; // Default template if not available
            const productId = label.articleId || label.articleList?.[0] || '';
            return `${storeCode};${labelCode};${templateName};${productId}`;
        });

        const linkContent = [header, ...rows].join('\n');

        // Download
        const blob = new Blob([linkContent], { type: 'text/plain;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; // EST local date
        const onlineSuffix = onlineOnly ? '-online' : '';
        link.setAttribute('href', url);
        link.setAttribute('download', `${globalSelectedStore?.store}_Rename_Necessary${onlineSuffix}-${timestamp}.link`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filter and sort fetched labels
    const filteredLabels = React.useMemo(() => {
        let result = [...fetchedLabels];

        // Online only filter
        // Logic: All assigned labels (online & offline) + Unassigned labels (online only)
        if (onlineOnly) {
            result = result.filter(l =>
                l._labelType === 'assigned' || l.networkStatus === true
            );
        }

        // Search filter
        if (labelSearchFilter) {
            const filter = labelSearchFilter.toLowerCase();
            result = result.filter(l =>
                l.labelCode?.toLowerCase().includes(filter) ||
                l.labelModel?.toLowerCase().includes(filter) ||
                l.gateway?.toLowerCase().includes(filter) ||
                l.articleId?.toLowerCase().includes(filter) ||
                l.articleList?.[0]?.toLowerCase().includes(filter)
            );
        }

        // Sort
        result.sort((a, b) => {
            let aVal, bVal;
            switch (labelSort.column) {
                case 'labelCode':
                    aVal = a.labelCode || '';
                    bVal = b.labelCode || '';
                    break;
                case 'articleId':
                    aVal = a.articleId || a.articleList?.[0] || '';
                    bVal = b.articleId || b.articleList?.[0] || '';
                    break;
                case 'labelModel':
                    aVal = a.labelModel || '';
                    bVal = b.labelModel || '';
                    break;
                case 'signal':
                    const signalOrder = { 'EXCELLENT': 4, 'GOOD': 3, 'NORMAL': 2, 'POOR': 1 };
                    aVal = signalOrder[a.signal] || 0;
                    bVal = signalOrder[b.signal] || 0;
                    break;
                case 'battery':
                    const batteryOrder = { 'GOOD': 3, 'NORMAL': 2, 'BAD': 1 };
                    aVal = batteryOrder[a.battery] || 0;
                    bVal = batteryOrder[b.battery] || 0;
                    break;
                case 'gateway':
                    aVal = a.gateway || '';
                    bVal = b.gateway || '';
                    break;
                case 'lastResponseTime':
                    aVal = new Date(a.lastResponseTime || 0).getTime();
                    bVal = new Date(b.lastResponseTime || 0).getTime();
                    break;
                case 'networkStatus':
                    aVal = a.networkStatus ? 1 : 0;
                    bVal = b.networkStatus ? 1 : 0;
                    break;
                default:
                    aVal = a.labelCode || '';
                    bVal = b.labelCode || '';
            }

            if (typeof aVal === 'number') {
                return labelSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            return labelSort.direction === 'asc'
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        });

        return result;
    }, [fetchedLabels, labelSearchFilter, labelSort, onlineOnly]);

    // Handle label sort
    const handleLabelSort = (column) => {
        setLabelSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Auto-download when fetch completes (if enabled)
    const prevLabelsLoadingRef = useRef(false);
    useEffect(() => {
        // Detect transition from loading to not loading with labels present
        if (prevLabelsLoadingRef.current && !labelsLoading && fetchedLabels.length > 0 && autoDownload) {
            // Small delay to ensure state is stable
            setTimeout(() => {
                // If produceLinkFile is enabled, download only the .link file; otherwise download CSV
                if (produceLinkFile) {
                    // Apply online-only filter if enabled
                    // Logic: All assigned labels (online & offline) + Unassigned labels (online only)
                    let labelsToExport = fetchedLabels;
                    if (onlineOnly) {
                        labelsToExport = fetchedLabels.filter(l =>
                            l._labelType === 'assigned' || l.networkStatus === true
                        );
                    }
                    exportLabelsLink(labelsToExport);
                } else {
                    exportLabelsCsv();
                }
            }, 100);
        }
        prevLabelsLoadingRef.current = labelsLoading;
    }, [labelsLoading, fetchedLabels, autoDownload, produceLinkFile, onlineOnly]);

    return (
        <>
            <div className="blink-led-container get-labels-container">
                {/* Left Panel - Configuration */}
                <div className="action-config-section blink-led-section">
                    <h3>Get All Labels</h3>
                    <div className="config-panel">
                        {/* Label Type Dropdown */}
                        <div className="config-row">
                            <label>Label Type:</label>
                            <select
                                className="label-type-select"
                                value={labelFilterType}
                                onChange={(e) => {
                                    setLabelFilterType(e.target.value);
                                    setFetchedLabels([]);
                                    setResults(null);
                                }}
                            >
                                <option value="all">All Labels</option>
                                <option value="assigned">Assigned Only</option>
                                <option value="unassigned">Unassigned Only</option>
                            </select>
                        </div>

                        {/* Fetch Button */}
                        <div className="config-row">
                            <button
                                className="execute-btn"
                                onClick={fetchLabels}
                                disabled={labelsLoading || !globalCompany || !globalSelectedStore}
                            >
                                {labelsLoading ? 'Fetching...' : `Fetch ${labelFilterType === 'all' ? 'All' : labelFilterType === 'assigned' ? 'Assigned' : 'Unassigned'} Labels`}
                            </button>
                        </div>

                        {/* Checkbox Options */}
                        <div className="config-row checkbox-options">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={autoDownload}
                                    onChange={(e) => setAutoDownload(e.target.checked)}
                                />
                                Automatically download
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={onlineOnly}
                                    onChange={(e) => setOnlineOnly(e.target.checked)}
                                />
                                Online only
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={produceLinkFile}
                                    onChange={(e) => setProduceLinkFile(e.target.checked)}
                                />
                                Produce .link file
                            </label>
                        </div>

                        {/* Export Buttons - shown immediately after checkboxes when labels exist */}
                        {fetchedLabels.length > 0 && (
                            <div className="config-row export-buttons-row">
                                <button
                                    className="execute-btn export-btn"
                                    onClick={exportLabelsCsv}
                                >
                                    📥 Export CSV ({(onlineOnly ? fetchedLabels.filter(l => l._labelType === 'assigned' || l.networkStatus === true).length : fetchedLabels.length).toLocaleString()} labels)
                                </button>
                                {produceLinkFile && (
                                    <button
                                        className="execute-btn export-link-btn"
                                        onClick={() => {
                                            let labelsToExport = fetchedLabels;
                                            if (onlineOnly) {
                                                labelsToExport = fetchedLabels.filter(l =>
                                                    l._labelType === 'assigned' || l.networkStatus === true
                                                );
                                            }
                                            exportLabelsLink(labelsToExport);
                                        }}
                                    >
                                        📄 Download .link File
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Results Summary */}
                        {results?.summary && (
                            <div className="config-row results-summary">
                                {results.summary.type === 'all' ? (
                                    <div className="summary-stats-grid">
                                        <div className="summary-stat large">
                                            <div className="summary-value">{results.summary.assignedCount?.toLocaleString() || 0}</div>
                                            <div className="summary-label">Assigned Labels</div>
                                        </div>
                                        <div className="summary-stat large">
                                            <div className="summary-value">{results.summary.unassignedCount?.toLocaleString() || 0}</div>
                                            <div className="summary-label">Unassigned Labels</div>
                                        </div>
                                        <div className="summary-stat large total">
                                            <div className="summary-value">{results.summary.total?.toLocaleString() || 0}</div>
                                            <div className="summary-label">Total Labels</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="summary-stat large">
                                        <div className="summary-value">{results.summary.total?.toLocaleString() || 0}</div>
                                        <div className="summary-label">
                                            {results.summary.type === 'assigned' ? 'Assigned Labels' : 'Unassigned Labels'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Errors */}
                        {errors.length > 0 && (
                            <div className="errors-list">
                                {errors.map((err, idx) => (
                                    <div key={idx} className="error-item">{err}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel - Labels Table */}
                <div className="action-results-section blink-led-results">
                    <h3>Labels ({filteredLabels.length.toLocaleString()}{labelSearchFilter ? ' filtered' : ''})</h3>

                    {/* Search Filter */}
                    <div className="table-controls">
                        <input
                            type="text"
                            placeholder="Search labels..."
                            value={labelSearchFilter}
                            onChange={(e) => { setLabelSearchFilter(e.target.value); setLabelsPage(1); }}
                            className="store-search-input"
                        />
                    </div>

                    {fetchedLabels.length > 0 ? (
                        <div className="store-batch-table-wrapper labels-table-wrapper">
                            <table className="store-batch-table labels-table">
                                <thead>
                                    <tr>
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('labelCode')}
                                        >
                                            Label Code {labelSort.column === 'labelCode' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        {labelFilterType === 'all' && (
                                            <th
                                                className="sortable-header"
                                                onClick={() => handleLabelSort('_labelType')}
                                            >
                                                Type {labelSort.column === '_labelType' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                            </th>
                                        )}
                                        {(labelFilterType === 'assigned' || labelFilterType === 'all') && (
                                            <th
                                                className="sortable-header"
                                                onClick={() => handleLabelSort('articleId')}
                                            >
                                                Article ID {labelSort.column === 'articleId' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                            </th>
                                        )}
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('labelModel')}
                                        >
                                            Model {labelSort.column === 'labelModel' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('signal')}
                                        >
                                            Signal {labelSort.column === 'signal' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('battery')}
                                        >
                                            Battery {labelSort.column === 'battery' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('gateway')}
                                        >
                                            Gateway {labelSort.column === 'gateway' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('lastResponseTime')}
                                        >
                                            Last Response {labelSort.column === 'lastResponseTime' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th
                                            className="sortable-header"
                                            onClick={() => handleLabelSort('networkStatus')}
                                        >
                                            Online {labelSort.column === 'networkStatus' && (labelSort.direction === 'asc' ? '▲' : '▼')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLabels.slice((labelsPage - 1) * LABELS_PER_PAGE, labelsPage * LABELS_PER_PAGE).map((label, idx) => (
                                        <tr key={label.labelCode || idx}>
                                            <td className="label-code">{label.labelCode}</td>
                                            {labelFilterType === 'all' && (
                                                <td><span className={`label-type-badge ${label._labelType}`}>{label._labelType === 'assigned' ? 'Assigned' : 'Unassigned'}</span></td>
                                            )}
                                            {(labelFilterType === 'assigned' || labelFilterType === 'all') && (
                                                <td>{label.articleId || label.articleList?.[0] || '-'}</td>
                                            )}
                                            <td className="label-model">{label.labelModel?.replace(/NEWTON_GRAPHIC_/i, '').replace(/_/g, ' ') || '-'}</td>
                                            <td><span className={`signal-${(label.signal || '').toLowerCase()}`}>{label.signal || '-'}</span></td>
                                            <td><span className={`battery-${(label.battery || '').toLowerCase()}`}>{label.battery || '-'}</span></td>
                                            <td className="gateway-code">{typeof label.gateway === 'string' && label.gateway ? label.gateway : '-'}</td>
                                            <td className="last-response">{label.lastResponseTime ? new Date(label.lastResponseTime.replace(' ', 'T')).toLocaleString() : '-'}</td>
                                            <td><span className={`online-status ${label.networkStatus === true ? 'online' : label.networkStatus === false ? 'offline' : 'na'}`}>{label.networkStatus === true ? 'Online' : label.networkStatus === false ? 'Offline' : 'N/A'}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : labelsLoading ? (
                        <div className="results-placeholder">
                            <div className="labels-loading-spinner" />
                            <p>Fetching labels, please wait…</p>
                        </div>
                    ) : (
                        <div className="results-placeholder">
                            <p>Select a company and store, then click "Fetch" to load labels.</p>
                        </div>
                    )}
                    {filteredLabels.length > 0 && (
                        <div className="labels-pagination">
                            <span className="labels-pagination-info">
                                {((labelsPage - 1) * LABELS_PER_PAGE + 1).toLocaleString()}–{Math.min(labelsPage * LABELS_PER_PAGE, filteredLabels.length).toLocaleString()} of {filteredLabels.length.toLocaleString()}{labelsLoading ? ' (loading…)' : ''}
                            </span>
                            <div className="labels-pagination-controls">
                                <button onClick={() => setLabelsPage(1)} disabled={labelsPage === 1}>«</button>
                                <button onClick={() => setLabelsPage(p => Math.max(1, p - 1))} disabled={labelsPage === 1}>‹</button>
                                {Array.from({ length: Math.ceil(filteredLabels.length / LABELS_PER_PAGE) }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === Math.ceil(filteredLabels.length / LABELS_PER_PAGE) || Math.abs(p - labelsPage) <= 2)
                                    .reduce((acc, p, i, arr) => {
                                        if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, i) => p === '…'
                                        ? <span key={`ellipsis-${i}`} className="labels-pagination-ellipsis">…</span>
                                        : <button key={p} onClick={() => setLabelsPage(p)} className={labelsPage === p ? 'active' : ''}>{p}</button>
                                    )}
                                <button onClick={() => setLabelsPage(p => Math.min(Math.ceil(filteredLabels.length / LABELS_PER_PAGE), p + 1))} disabled={labelsPage >= Math.ceil(filteredLabels.length / LABELS_PER_PAGE)}>›</button>
                                <button onClick={() => setLabelsPage(Math.ceil(filteredLabels.length / LABELS_PER_PAGE))} disabled={labelsPage >= Math.ceil(filteredLabels.length / LABELS_PER_PAGE)}>»</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default GetAllLabelsPanel;
